<template>
  <div class="upgrade-plan">
    <div class="upgrade-plan-container">
      <h1 class="upgrade-plan-title">Повышение лимита</h1>
      <p class="upgrade-plan-subtitle">Выберите категорию клиента и условия торговли</p>

      <!-- Professional application in progress — three cases, in order
           of precedence: an unfinished draft (resume), a fully submitted
           review (wait), each takes priority over the Qualified banner
           because the two paths are mutually exclusive. -->
      <!-- PROFESSIONAL request was rejected — red banner with the
           per-doc MLRO reasons + a CTA into the professional wizard
           so the client can submit a new package. -->
      <div v-if="professionalRejected" class="upgrade-plan-sof-banner upgrade-plan-sof-banner-danger">
        <b-icon icon="x-octagon-fill" class="upgrade-plan-sof-icon upgrade-plan-sof-icon-danger" />
        <div class="upgrade-plan-sof-body">
          <div class="upgrade-plan-sof-title">Заявка на статус профессионального клиента отклонена</div>
          <div class="upgrade-plan-sof-text">
            <span v-if="!professionalRejectionReasons.length">
              Ваша заявка на статус профессионального клиента была отклонена.
              Вы можете подать новую заявку.
            </span>
            <template v-else>
              <div style="margin-bottom:6px;">Причины отказа:</div>
              <ul class="upgrade-plan-reject-list">
                <li v-for="(r, i) in professionalRejectionReasons" :key="i">
                  <strong>{{ r.label }}</strong><template v-if="r.reason">: {{ r.reason }}</template>
                </li>
              </ul>
            </template>
          </div>
        </div>
        <b-button variant="danger" size="sm" pill class="upgrade-plan-sof-btn"
                  @click="$router.push('/professional')">
          Подать заявку заново
        </b-button>
      </div>
      <div v-else-if="professionalDraft" class="upgrade-plan-sof-banner">
        <b-icon icon="arrow-right-circle" class="upgrade-plan-sof-icon" />
        <div class="upgrade-plan-sof-body">
          <div class="upgrade-plan-sof-title">Заявка Professional не завершена</div>
          <div class="upgrade-plan-sof-text">
            Вы начали оформление заявки на статус Professional, но не
            подтвердили осознание рисков на последнем шаге. Продолжите
            с того места, где остановились.
          </div>
        </div>
        <b-button variant="warning" size="sm" pill class="upgrade-plan-sof-btn"
                  @click="$router.push('/professional')">
          Продолжить
        </b-button>
      </div>
      <div v-else-if="professionalSubmitted" class="upgrade-plan-sof-banner">
        <b-icon icon="patch-check-fill" class="upgrade-plan-sof-icon" />
        <div class="upgrade-plan-sof-body">
          <div class="upgrade-plan-sof-title">Заявка на статус Professional отправлена</div>
          <div class="upgrade-plan-sof-text">
            Уважаемый Клиент! Благодарим за предоставленные документы, в
            настоящее время они находятся на проверке. Ожидайте обратной связи.
          </div>
        </div>
        <b-button variant="warning" size="sm" pill class="upgrade-plan-sof-btn"
                  @click="$router.push('/professional')">
          Открыть документы
        </b-button>
      </div>

      <!-- Latest RETAIL_Q request was rejected — distinct red banner
           with the per-doc MLRO reasons + a CTA into the qualified
           flow's resubmit screen. -->
      <div v-else-if="qualifiedRejected" class="upgrade-plan-sof-banner upgrade-plan-sof-banner-danger">
        <b-icon icon="x-octagon-fill" class="upgrade-plan-sof-icon upgrade-plan-sof-icon-danger" />
        <div class="upgrade-plan-sof-body">
          <div class="upgrade-plan-sof-title">Заявка на статус розничного клиента отклонена</div>
          <div class="upgrade-plan-sof-text">
            <span v-if="!rejectionReasons.length">
              Ваша заявка на статус квалифицированного клиента была отклонена.
              Вы можете подать новую заявку.
            </span>
            <template v-else>
              <div style="margin-bottom:6px;">Причины отказа:</div>
              <ul class="upgrade-plan-reject-list">
                <li v-for="(r, i) in rejectionReasons" :key="i">
                  <strong>{{ r.label }}</strong><template v-if="r.reason">: {{ r.reason }}</template>
                </li>
              </ul>
            </template>
          </div>
        </div>
        <b-button variant="danger" size="sm" pill class="upgrade-plan-sof-btn"
                  @click="goToQualified">
          Подать заявку заново
        </b-button>
      </div>

      <!-- Surfaces whenever the user has any qualified-related artefact
           on file (passed test, cert, SoF) but hasn't been promoted to
           RETAIL_Q yet. The exact copy + CTA depend on what's still
           missing vs what's already pending review. Hidden while a
           Professional package is in flight — parallel paths are blocked. -->
      <div v-else-if="qualifiedPending" class="upgrade-plan-sof-banner">
        <b-icon icon="patch-check-fill" class="upgrade-plan-sof-icon" />
        <div class="upgrade-plan-sof-body">
          <div class="upgrade-plan-sof-title">{{ qualifiedBannerTitle }}</div>
          <div class="upgrade-plan-sof-text">{{ qualifiedBannerText }}</div>
        </div>
        <b-button variant="warning" size="sm" pill class="upgrade-plan-sof-btn"
                  @click="goToQualified">
          {{ qualifiedBannerCta }}
        </b-button>
      </div>

      <div class="upgrade-plan-cards">
        <!-- Qualified client -->
        <div class="upgrade-card upgrade-card-qualified">
          <div class="upgrade-card-header">
            <span class="upgrade-card-badge">Retail Client (assessed)</span>
            <h2 class="upgrade-card-title">Розничный клиент</h2>
          </div>
          <p class="upgrade-card-desc">
            Клиенты с подтверждённым опытом и более углубленными знаниями. Лимит инвестирования — до <strong>{{ qualifiedLimitText }} USD</strong>.
          </p>
          <div class="upgrade-card-instructions">
            <p class="upgrade-card-instructions-title">Что необходимо сделать:</p>
            <ol class="upgrade-card-instructions-list">
              <li><strong>Этап 1.</strong> Подтвердить свою квалификацию одним из двух способов: успешно сдать расширенный тест на понимание рисков либо предоставить действующие сертификаты <strong>CFA / FRM / CAIA / CIIA</strong>.</li>
              <li><strong>Этап 2.</strong> Прикрепить документы, подтверждающие источник происхождения средств (SoF) и источник благосостояния (SoW).</li>
              <li><strong>Этап 3.</strong> Ожидать подтверждения заявки со стороны payton.kz.</li>
            </ol>
            <p class="upgrade-card-instructions-note">Подгружаемые документы должны быть в формате <strong>PDF / JPG / PNG / ZIP / RAR</strong>, размер каждого файла по отдельности не должен превышать <strong>10 МБ</strong>.</p>
          </div>
          <div class="upgrade-card-cta">
            <b-button
              :variant="qualifiedRejected ? 'danger' : 'warning'"
              pill
              class="upgrade-btn"
              :disabled="qualifiedBtnDisabled"
              @click="goToQualified"
            >
              <b-icon :icon="qualifiedCardIcon" class="mr-2"></b-icon>
              {{ qualifiedCardCta }}
            </b-button>
          </div>
        </div>

        <!-- Expert / Professional client -->
        <div class="upgrade-card upgrade-card-expert">
          <div class="upgrade-card-header">
            <span class="upgrade-card-badge">Professional Client (assessed)</span>
            <h2 class="upgrade-card-title">Профессиональный клиент</h2>
          </div>
          <p class="upgrade-card-desc">
            Клиенты с богатым опытом работы на рынке цифровых активов. Лимит инвестирования — до <strong>{{ professionalLimitText }} USD</strong>.
          </p>
          <div class="upgrade-card-instructions">
            <p class="upgrade-card-instructions-title">Что необходимо сделать:</p>
            <ol class="upgrade-card-instructions-list">
              <li><strong>Этап 1.</strong> Пройти тест, направленный на оценку практического опыта и понимания рисков.</li>
              <li><strong>Этап 2.</strong> Подтвердить опыт торговли цифровыми активами, приложив историю торгов (отчёты бирж и/или брокеров, прочие документы).</li>
              <li><strong>Этап 3.</strong> Прикрепить документы, подтверждающие источник происхождения средств (SoF) и источник благосостояния (SoW).</li>
              <li><strong>Этап 4.</strong> Подтвердить квалификацию, предоставив образовательные и профессиональные сертификаты (опционально).</li>
              <li><strong>Этап 5.</strong> Подтвердить осознание рисков и снижение защитных мер.</li>
              <li><strong>Этап 6.</strong> Ожидать подтверждения заявки со стороны payton.kz.</li>
            </ol>
            <p class="upgrade-card-instructions-note">Подгружаемые документы должны быть в формате <strong>PDF / JPG / PNG / ZIP / RAR</strong>, размер каждого файла по отдельности не должен превышать <strong>10 МБ</strong>.</p>
          </div>
          <div class="upgrade-card-cta">
            <b-button
              :variant="professionalRejected ? 'danger' : 'outline-light'"
              pill
              class="upgrade-btn"
              :disabled="professionalBtnDisabled"
              @click="goToExpert"
            >
              <b-icon :icon="professionalBtnIcon" class="mr-2"></b-icon>
              {{ professionalBtnLabel }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Professional client document upload modal -->
    <b-modal
      id="professional-upload-modal"
      title="Загрузить документы (Professional Client)"
      size="lg"
      body-class="professional-upload-modal-body"
      header-class="professional-upload-modal-header"
      footer-class="professional-upload-modal-footer"
      hide-header-close
      @show="onProfessionalModalShow"
      @hidden="onProfessionalModalHidden"
    >
      <div class="professional-upload-inner">
        <p class="professional-upload-desc mb-3">
          Выберите файл (до 10 МБ) с документами: SoF, SoW, торговая история, при необходимости — сертификаты. Поддерживаются PDF, JPG, PNG, ZIP, RAR.
        </p>
        <div
          class="professional-upload-zone"
          :class="{
            'professional-upload-zone-dragover': professionalDragOver,
            'professional-upload-zone-has-file': professionalFile
          }"
          @dragover.prevent="professionalDragOver = true"
          @dragleave.prevent="professionalDragOver = false"
          @drop.prevent="onProfessionalDrop"
          @click="!professionalFile && $refs.professionalFileInput && $refs.professionalFileInput.click()"
        >
          <input
            ref="professionalFileInput"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.zip,.rar"
            class="professional-upload-input"
            @change="onProfessionalFileSelect"
          />
          <template v-if="!professionalFile">
            <b-icon icon="cloud-arrow-up" class="professional-upload-icon"></b-icon>
            <p class="professional-upload-text">Перетащите файл сюда или нажмите для выбора</p>
            <p class="professional-upload-hint">PDF, JPG, PNG, ZIP или RAR. Не более 10 МБ.</p>
          </template>
          <template v-else>
            <b-icon icon="file-earmark-zip" class="professional-upload-icon"></b-icon>
            <p class="professional-upload-filename">{{ professionalFile.name }}</p>
            <button
              type="button"
              class="professional-upload-remove"
              @click.stop="clearProfessionalFile"
            >
              Удалить и выбрать другой
            </button>
          </template>
        </div>
        <!--
          Annex №3 §5 of the Regulation: Professional Client status can
          only be granted after the user explicitly acknowledges these
          two risks. Without both checkboxes the upload button stays
          disabled and the backend will reject the upload anyway.
        -->
        <div class="professional-risk-consents mt-3">
          <b-form-checkbox v-model="professionalCapitalLossRisk" class="professional-risk-checkbox">
            Я понимаю риск полной потери вложенных средств.
          </b-form-checkbox>
          <b-form-checkbox v-model="professionalVolatilityRisk" class="professional-risk-checkbox mt-2">
            Я понимаю риск высокой волатильности цифровых активов.
          </b-form-checkbox>
        </div>

        <div v-if="professionalError" class="professional-upload-error mt-3">
          <b-icon icon="exclamation-circle"></b-icon>
          {{ professionalError }}
        </div>

        <div class="professional-doc-list mt-4">
          <div class="professional-doc-list-head">
            <h6 class="professional-doc-list-title">Загруженные документы</h6>
            <b-button
              variant="outline-light"
              size="sm"
              class="professional-doc-list-refresh-btn"
              :disabled="clientDocListLoading"
              @click="refreshClientDocList"
              title="Обновить список"
            >
              <b-icon icon="arrow-repeat" :animation="clientDocListLoading ? 'spin' : null"></b-icon>
            </b-button>
          </div>
          <div v-if="clientDocListLoading" class="professional-doc-list-loading">
            <b-spinner small></b-spinner>
            <span>Загрузка списка…</span>
          </div>
          <div v-else-if="!clientDocList || clientDocList.length === 0" class="professional-doc-list-empty">
            Нет загруженных документов
          </div>
          <div v-else class="professional-doc-list-table-wrap">
            <table class="professional-doc-list-table">
              <thead>
                <tr>
                  <th>Статус</th>
                  <th>Файл</th>
                  <th>Комментарий</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(doc, idx) in clientDocList" :key="doc.docId != null ? doc.docId : idx">
                  <td><span :class="['professional-doc-status', 'professional-doc-status-' + (doc.docStatus || '').toLowerCase()]">{{ doc.docStatus || '—' }}</span></td>
                  <td class="professional-doc-path">{{ doc.docPath || '—' }}</td>
                  <td class="professional-doc-mlro">{{ doc.mlroDesc || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <b-button variant="outline-secondary" size="sm" @click="$bvModal.hide('professional-upload-modal')">
          Отмена
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!professionalFile || professionalUploading || !professionalCapitalLossRisk || !professionalVolatilityRisk"
          @click="uploadProfessionalDocument"
        >
          <b-spinner v-if="professionalUploading" small class="mr-2"></b-spinner>
          {{ professionalUploading ? 'Загрузка…' : 'Отправить на проверку' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader, getMultipartHeader } from '@/config/config'
import { mapState } from 'vuex'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

export default {
  name: 'UpgradePlan',
  data() {
    return {
      loadedUserData: {},
      professionalFile: null,
      professionalDragOver: false,
      professionalUploading: false,
      professionalError: null,
      // Annex №3 §5: Professional Client request needs explicit acknowledgement
      // of capital-loss risk and high-volatility risk before MLRO can approve.
      professionalCapitalLossRisk: false,
      professionalVolatilityRisk: false,
      clientDocList: [],
      clientDocListLoading: false,
      // Qualified-status artefacts (separate by docType per backend).
      // Drive the banner + alt CTA on this page.
      sofDocs: [],
      // Latest RETAIL_Q classification request — drives the REJECTED
      // banner and rejection reasons surfaced on the upgrade page.
      request: null,
      // Same idea for PROFESSIONAL.
      professionalRequest: null,
      certDocs: [],
      // Risk consents — mirror of lms_core_user flags. Drives the
      // draft-vs-submitted split for the Professional card and banner.
      capitalLossRiskAccepted: false,
      volatilityRiskAccepted: false,
      // Настраиваемые максимальные лимиты по категориям (из админской
      // «Конфигурации», p_classification_limit). Приходят в /ptest/clientstatus.
      classificationLimits: {},
    }
  },
  computed: {
    ...mapState(['loginedUser']),
    // Форматированные максимальные лимиты для карточек. Значение берём из
    // конфигурации (classificationLimits), при её отсутствии — прежние
    // значения по умолчанию, чтобы карточки не «схлопывались» при сбое.
    qualifiedLimitText() {
      return this.formatLimit(this.classificationLimits.RETAIL_Q, 100000)
    },
    professionalLimitText() {
      return this.formatLimit(this.classificationLimits.PROFESSIONAL, 5000000)
    },
    isQualified() {
      // "isQualified" in this context means "at least Retail-Q", which
      // includes Professional too — the upgrade-flow uses it to disable
      // the qualified button when the user is already past that level.
      const cat = this.loadedUserData.clientCategory
      return cat === 'RETAIL_Q' || cat === 'PROFESSIONAL'
    },
    isProfessional() {
      return this.loadedUserData.clientCategory === 'PROFESSIONAL'
    },
    qualifiedBtnDisabled() {
      // Block parallel applications: any Professional activity (draft,
      // submitted, or REJECTED-but-not-yet-resubmitted) locks the
      // Qualified path. The user must finish (or cancel) the Professional
      // flow before starting a different category.
      return this.isQualified || this.isProfessional || this.professionalPending || this.professionalRejected
    },
    hasProfessionalDocs() {
      return Array.isArray(this.clientDocList) && this.clientDocList.length > 0
    },
    professionalConsentsAccepted() {
      return this.capitalLossRiskAccepted && this.volatilityRiskAccepted
    },
    // A Professional application is "submitted" only once the user has
    // both uploaded paperwork AND ticked the two risk consents on the
    // final step. Up to that point it's a draft — files exist on the
    // server but the user can still resume the wizard.
    professionalSubmitted() {
      if (this.isProfessional) return false
      if (this.professionalRejected) return false
      return this.hasProfessionalDocs && this.professionalConsentsAccepted
    },
    professionalDraft() {
      if (this.isProfessional) return false
      if (this.professionalRejected) return false
      return this.hasProfessionalDocs && !this.professionalConsentsAccepted
    },
    professionalPending() {
      // Legacy alias kept for any external check; matches the union of
      // draft and submitted (anything that's in the Professional flow).
      return this.professionalDraft || this.professionalSubmitted
    },

    // True when ANY qualified-related artefact is on file (passed test,
    // SoF doc, certificate) but the user hasn't been promoted to
    // RETAIL_Q yet. The banner + alternate CTA copy only show in this
    // state — once the manager approves and clientCategory flips, the
    // whole upgrade card returns to its default "stay where you are"
    // appearance for higher tiers.
    qualifiedPassed() {
      const v = this.loadedUserData && this.loadedUserData.isPassedQualified
      return v === 1 || v === true
    },
    hasSofDocs()  { return Array.isArray(this.sofDocs)  && this.sofDocs.length  > 0 },
    hasCertDocs() { return Array.isArray(this.certDocs) && this.certDocs.length > 0 },
    // True when the latest RETAIL_Q request landed in REJECTED. We
    // show a distinct red banner with the per-doc MLRO comments so
    // the client immediately knows what went wrong and gets the
    // "Подать заявку заново" CTA.
    qualifiedRejected() {
      const r = this.request
      return !!r && r.status === 'REJECTED' && r.requestedCategory === 'RETAIL_Q'
    },
    rejectionReasons() {
      return this._reasonsFromRequest(this.request)
    },
    professionalRejected() {
      const r = this.professionalRequest
      return !!r && r.status === 'REJECTED' && r.requestedCategory === 'PROFESSIONAL'
    },
    professionalRejectionReasons() {
      return this._reasonsFromRequest(this.professionalRequest)
    },
    qualifiedPending() {
      if (this.isQualified || this.isProfessional) return false
      // Rejected applications take priority over the "pending review"
      // copy — the REJECTED banner is its own block.
      if (this.qualifiedRejected) return false
      // Mutual exclusion: if the user is on the Professional track (draft
      // or submitted), we don't surface a Qualified-flavoured pending
      // state, even if isPassedQualified=1 from the shared test bank.
      if (this.professionalDraft || this.professionalSubmitted) return false
      return this.qualifiedPassed || this.hasSofDocs || this.hasCertDocs
    },

    // Banner copy varies with what's still missing:
    //   - SoF already uploaded     → everything in flight, just wait.
    //   - Cert uploaded, no SoF    → finish step 2 (upload SoF).
    //   - Passed test, nothing yet → upload SoF.
    qualifiedBannerTitle() {
      if (this.hasSofDocs) return 'Документы отправлены'
      if (this.hasCertDocs) return 'Сертификат отправлен'
      return 'Вы успешно прошли квалификационный тест'
    },
    qualifiedBannerText() {
      if (this.hasSofDocs) {
        return 'Уважаемый Клиент! Благодарим за предоставленные документы, ' +
          'в настоящее время они находятся на проверке. Ожидайте обратной связи.'
      }
      if (this.hasCertDocs) {
        return 'Сертификат находится на проверке. Остался последний шаг — ' +
          'загрузите документы, подтверждающие источник средств (SoF).'
      }
      return 'Остался последний шаг — загрузите документы, подтверждающие ' +
        'источник средств. Если уже загрузили — ожидайте обратной связи.'
    },
    qualifiedBannerCta() {
      if (this.hasSofDocs) return 'Открыть документы'
      if (this.hasCertDocs) return 'Загрузить SoF'
      return 'Загрузить документы'
    },

    // Same logic surfaced on the Qualified card itself.
    qualifiedCardAction() {
      if (!this.qualifiedPending) {
        return 'Пройдите тест, чтобы стать квалифицированным клиентом — и получите возможность совершать операции до 100 000 USD в месяц при подтверждении источника происхождения средств.'
      }
      if (this.hasSofDocs) {
        return 'Уважаемый Клиент! Благодарим за предоставленные документы, в настоящее время они находятся на проверке. Ожидайте обратной связи.'
      }
      if (this.hasCertDocs) {
        return 'Сертификат принят, осталось загрузить SoF. После проверки лимит будет увеличен до 100 000 USD/мес.'
      }
      return 'Тест уже пройден. Чтобы получить статус и лимит до 100 000 USD в месяц — загрузите документы, подтверждающие источник средств.'
    },
    qualifiedCardCta() {
      if (this.isQualified) return 'Уже квалифицированный клиент'
      if (this.isProfessional) return 'Уже профессиональный клиент'
      if (this.professionalPending) return 'Подана заявка на профессионального клиента'
      if (this.qualifiedRejected) return 'Подать заявку заново'
      if (this.hasSofDocs) return 'Открыть документы'
      if (this.hasCertDocs) return 'Загрузить SoF'
      if (this.qualifiedPassed) return 'Загрузить документы (SoF)'
      return 'Стать квалифицированным клиентом'
    },
    qualifiedCardIcon() {
      if (this.qualifiedRejected) return 'x-octagon-fill'
      if (this.hasSofDocs)  return 'hourglass-split'
      if (this.hasCertDocs) return 'cloud-arrow-up'
      if (this.qualifiedPassed) return 'cloud-arrow-up'
      return 'patch-check'
    },
    professionalBtnDisabled() {
      // Locked when already promoted, when a Qualified package is being
      // reviewed or REJECTED-but-not-yet-resubmitted, or when the
      // Professional application is fully submitted (consents in).
      // A draft Professional application keeps the button live so the
      // user can click "Продолжить".
      return this.isProfessional || this.qualifiedPending || this.qualifiedRejected || this.professionalSubmitted
    },
    professionalBtnLabel() {
      if (this.isProfessional) return 'Уже профессиональный клиент'
      if (this.professionalSubmitted) return 'Заявка отправлена'
      if (this.professionalDraft) return 'Продолжить оформление'
      if (this.qualifiedPending) return 'Подана заявка на квалифицированного клиента'
      if (this.professionalRejected) return 'Подать заявку заново'
      return 'Стать профессиональным клиентом'
    },
    professionalBtnIcon() {
      if (this.isProfessional) return 'patch-check-fill'
      if (this.professionalSubmitted) return 'hourglass-split'
      if (this.professionalDraft) return 'arrow-right-circle'
      if (this.qualifiedPending) return 'hourglass-split'
      if (this.professionalRejected) return 'x-octagon-fill'
      return 'file-earmark-zip'
    },
  },
  mounted() {
    this.fetchUserDetails()
    this.fetchQualifiedArtefacts()
    // Load the Professional doc list up front so the Qualified card can
    // tell whether a Professional package is already pending and disable
    // itself accordingly (parallel applications are not allowed).
    this.fetchClientDocList()
  },
  methods: {
    // Число → строка с разделителями тысяч ("100 000"). При пустом/битом
    // значении из конфигурации используем fallback.
    formatLimit(value, fallback) {
      const n = Number(value)
      const v = Number.isFinite(n) && n > 0 ? n : fallback
      return v.toLocaleString('ru-RU')
    },
    fetchUserDetails() {
      if (!this.loginedUser || !this.loginedUser.id) return
      axios
        .get(`${apiDomain}/exchange/userdetails/${this.loginedUser.id}`, { headers: getHeader() })
        .then(res => {
          this.loadedUserData = res.data || {}
          // Guard: the upgrade flow assumes the post-AML onboarding
          // survey is on file. If the user landed here by typing the
          // URL or following a stale link, bounce them back to the
          // landing where the CTA is correctly hidden.
          const d = this.loadedUserData
          const surveyDone = d.isSurveyShown === 1 || d.isSurveyShown === true
          const hasCategory = d.clientCategory && d.clientCategory !== 'NONE'
          if (!surveyDone || !hasCategory) {
            this.$router.replace('/landing').catch(() => {})
          }
        })
        .catch(() => {})
    },
    fetchQualifiedArtefacts() {
      // /ptest/clientstatus rolls every upgrade-pipeline fact into a
      // single response — clientCategory, isPassedQualified, sof and
      // cert lists, both classification requests. Failure leaves the
      // local arrays empty, which makes the banner/CTA fall back to
      // the "haven't started" copy.
      const headers = getHeader()
      if (!headers) return
      axios.get(`${apiDomain}/ptest/clientstatus`, { headers })
        .then(res => {
          const d = res.data || {}
          this.sofDocs = Array.isArray(d.sofDocs) ? d.sofDocs : []
          this.certDocs = Array.isArray(d.certDocs) ? d.certDocs : []
          this.request = d.request || null
          this.professionalRequest = d.professionalRequest || null
          this.capitalLossRiskAccepted = !!d.isCapitalLossRiskAccepted
          this.volatilityRiskAccepted  = !!d.isVolatilityRiskAccepted
          this.classificationLimits = d.classificationLimits || {}
        })
        .catch(() => {
          this.sofDocs = []
          this.certDocs = []
          this.request = null
          this.professionalRequest = null
          this.capitalLossRiskAccepted = false
          this.volatilityRiskAccepted = false
          this.classificationLimits = {}
        })
    },
    goToQualified() {
      this.$router.push('/qualified').catch(() => {})
    },
    _reasonsFromRequest(req) {
      // In the one-request-per-category model the same request can
      // accumulate multiple uploads per docType (REJECTED → user
      // replaces → new PENDING upload sits alongside the old REJECTED).
      // Reasons for OLD rejected docs are obsolete — the user has
      // already addressed them. So: for each docType, look only at the
      // freshest doc; surface its rejection reason only if it's still
      // REJECTED.
      const docs = (req && req.documents) || []
      const latest = new Map()
      docs.forEach(d => {
        if (!d || !d.docType) return
        const prev = latest.get(d.docType)
        if (!prev || (d.docId || 0) > (prev.docId || 0)) latest.set(d.docType, d)
      })
      const out = []
      latest.forEach(d => {
        if (d.docStatus !== 'REJECTED') return
        const reason = d.mlroDesc ? String(d.mlroDesc).trim() : ''
        out.push({ docType: d.docType, label: this._docTypeLabel(d.docType), reason })
      })
      return out
    },
    _docTypeLabel(t) {
      const map = {
        QUALIFIED:           'Источник происхождения средств (SoF)',
        CERTIFICATE:         'Сертификат (CFA / FRM / CAIA / CIIA)',
        PROFESSIONAL_HISTORY:'История торгов',
        PROFESSIONAL_SOF:    'Источник средств (SoF / SoW)',
        PROFESSIONAL_CERT:   'Сертификат / диплом',
        PROFESSIONAL:        'Архив документов',
      }
      return map[t] || t || 'Документ'
    },
    goToExpert() {
      // The legacy single-archive modal is still mounted as a fallback,
      // but the Professional path is now driven by the stepper wizard at
      // /professional (mirrors /qualified).
      this.$router.push('/professional').catch(() => {})
    },
    onProfessionalModalShow() {
      this.refreshClientDocList()
    },
    refreshClientDocList() {
      this.fetchClientDocList()
    },
    fetchClientDocList() {
      this.clientDocListLoading = true
      this.clientDocList = []
      const url = `${apiDomain}/ptest/clientdoclist/PROFESSIONAL`
      const headers = getHeader()
      if (!headers) {
        this.clientDocListLoading = false
        return
      }
      axios
        .get(url, { headers })
        .then(res => {
          const data = res.data
          this.clientDocList = Array.isArray(data) ? data : (data && (data.list || data.data)) || []
        })
        .catch(() => {
          this.clientDocList = []
        })
        .finally(() => {
          this.clientDocListLoading = false
        })
    },
    onProfessionalFileSelect(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.setProfessionalFile(file)
      e.target.value = ''
    },
    onProfessionalDrop(e) {
      this.professionalDragOver = false
      const file = e.dataTransfer.files && e.dataTransfer.files[0]
      if (!file) return
      this.setProfessionalFile(file)
    },
    setProfessionalFile(file) {
      this.professionalError = null
      const name = (file.name || '').toLowerCase()
      const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.zip', '.rar']
      if (!allowed.some(ext => name.endsWith(ext))) {
        this.professionalError = 'Разрешены PDF, JPG, PNG, ZIP или RAR.'
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        this.professionalError = 'Файл не должен превышать 10 МБ.'
        return
      }
      this.professionalFile = file
    },
    clearProfessionalFile() {
      this.professionalFile = null
      this.professionalError = null
      if (this.$refs.professionalFileInput) this.$refs.professionalFileInput.value = ''
    },
    uploadProfessionalDocument() {
      if (!this.professionalFile) return
      this.professionalError = null
      this.professionalUploading = true
      const formData = new FormData()
      formData.append('docType', 'PROFESSIONAL')
      formData.append('clientDoc', this.professionalFile)
      formData.append('capitalLossRisk', this.professionalCapitalLossRisk ? 'true' : 'false')
      formData.append('volatilityRisk', this.professionalVolatilityRisk ? 'true' : 'false')
      const url = `${apiDomain}/ptest/postclientfile`
      const baseHeaders = getHeader()
      if (!baseHeaders) {
        this.professionalError = 'Необходимо войти в аккаунт.'
        this.professionalUploading = false
        return
      }
      const headers = { Authorization: baseHeaders.Authorization }
      axios
        .post(url, formData, { headers })
        .then(res => {
          const ok = res.status === 200 && (
            res.data === 'success' ||
            (res.data && (res.data.success === true || res.data.status === 'success'))
          )
          if (!ok) {
            this.professionalError = (res.data && (res.data.message || res.data.msg)) || 'Загрузка не удалась.'
            return
          }
          this.$bvToast.toast('Уважаемый Клиент! Благодарим за предоставленные документы, в настоящее время они находятся на проверке. Ожидайте обратной связи.', {
            title: 'Успешно',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-center',
          })
          this.refreshClientDocList()
          this.$bvModal.hide('professional-upload-modal')
          this.clearProfessionalFile()
        })
        .catch(err => {
          const msg =
            (err.response && err.response.data && (err.response.data.message || err.response.data.msg)) ||
            err.message ||
            'Не удалось загрузить файл. Попробуйте позже.'
          this.professionalError = msg
        })
        .finally(() => {
          this.professionalUploading = false
        })
    },
    onProfessionalModalHidden() {
      this.clearProfessionalFile()
      this.professionalDragOver = false
      this.professionalUploading = false
      this.professionalCapitalLossRisk = false
      this.professionalVolatilityRisk = false
      this.clientDocList = []
    },
  },
}
</script>

<style scoped>
.upgrade-plan {
  min-height: 70vh;
  padding: 48px 24px 64px;
  background: linear-gradient(180deg, #0a0a0f 0%, #14141a 100%);
}
.upgrade-plan-container {
  max-width: 960px;
  margin: 0 auto;
}
.upgrade-plan-title {
  color: #f8fafc;
  font-size: 1.85rem;
  font-weight: 900;
  text-align: center;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}
.upgrade-plan-subtitle {
  color: rgba(255, 255, 255, 0.65);
  font-size: 1rem;
  text-align: center;
  margin-bottom: 40px;
}
.upgrade-plan-sof-banner {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 22px;
  margin-bottom: 28px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(239, 185, 20, 0.15), rgba(239, 185, 20, 0.05));
  border: 1px solid rgba(239, 185, 20, 0.5);
  color: rgba(255, 255, 255, 0.92);
}
.upgrade-plan-sof-icon {
  flex-shrink: 0;
  font-size: 2rem;
  color: #EFB914;
}
/* Rejected state — same layout, swapped to a red palette across the
   whole banner (background, border, icon) so it reads as a problem
   the user needs to act on rather than a "waiting" state. */
.upgrade-plan-sof-banner-danger {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.18), rgba(239, 68, 68, 0.05));
  border-color: rgba(239, 68, 68, 0.55);
}
.upgrade-plan-sof-icon-danger { color: #F87171; }
.upgrade-plan-reject-list {
  margin: 0;
  padding-left: 18px;
  color: rgba(255, 255, 255, 0.85);
}
.upgrade-plan-reject-list li { margin-bottom: 2px; }
.upgrade-plan-sof-body {
  flex: 1;
  min-width: 0;
}
.upgrade-plan-sof-title {
  font-weight: 800;
  font-size: 1rem;
  margin-bottom: 4px;
}
.upgrade-plan-sof-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.5;
}
.upgrade-plan-sof-btn {
  flex-shrink: 0;
  white-space: nowrap;
}
@media (max-width: 640px) {
  .upgrade-plan-sof-banner {
    flex-direction: column;
    align-items: flex-start;
  }
  .upgrade-plan-sof-btn {
    align-self: stretch;
  }
}

.upgrade-plan-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
}
@media (max-width: 768px) {
  .upgrade-plan-cards {
    grid-template-columns: 1fr;
  }
}
.upgrade-card {
  border-radius: 24px;
  padding: 28px 26px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(145deg, rgba(37, 40, 64, 0.6) 0%, rgba(20, 22, 35, 0.8) 100%);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.upgrade-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 56px rgba(0, 0, 0, 0.4);
}
.upgrade-card-header {
  margin-bottom: 16px;
}
.upgrade-card-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 8px;
}
.upgrade-card-qualified .upgrade-card-badge {
  color: rgba(239, 185, 20, 0.95);
}
.upgrade-card-expert .upgrade-card-badge {
  color: rgba(99, 102, 241, 0.95);
}
.upgrade-card-title {
  color: #f8fafc;
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
}
.upgrade-card-desc {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
  flex: 1;
}
.upgrade-card-action {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.55;
  margin-bottom: 22px;
}
.upgrade-card-action strong {
  color: #EFB914;
  font-weight: 800;
}
.upgrade-card-instructions {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.upgrade-card-instructions-title {
  color: rgba(255, 255, 255, 0.95);
  font-size: 13px;
  font-weight: 800;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.upgrade-card-instructions-list {
  color: rgba(255, 255, 255, 0.88);
  font-size: 13px;
  line-height: 1.55;
  margin: 0 0 12px 0;
  padding-left: 0;
  /* Each item already starts with "Этап N." in copy — drop the native
     <ol> marker to avoid "1. Этап 1." duplication. */
  list-style: none;
}
.upgrade-card-instructions-list li {
  margin-bottom: 6px;
}
.upgrade-card-instructions-list ul {
  list-style: none;
  padding-left: 0;
  margin: 6px 0 0 0;
}
.upgrade-card-instructions-list ul li {
  position: relative;
  padding-left: 14px;
  margin-bottom: 4px;
}
.upgrade-card-instructions-list ul li::before {
  content: '–';
  position: absolute;
  left: 0;
  color: rgba(255, 255, 255, 0.6);
}
.upgrade-card-instructions-list strong {
  color: #EFB914;
  font-weight: 700;
}
.upgrade-card-instructions-note {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.upgrade-card-cta {
  margin-top: auto;
}
.upgrade-btn {
  font-weight: 800;
  padding: 12px 22px;
  font-size: 14px;
}
.upgrade-card-qualified .upgrade-btn {
  background: linear-gradient(180deg, #EFB914, rgba(239, 185, 20, 0.85));
  border-color: #EFB914;
  color: #000;
}
.upgrade-card-qualified .upgrade-btn:hover {
  filter: brightness(1.08);
  color: #000;
}
.upgrade-card-expert .upgrade-btn {
  border-width: 1.5px;
}
.upgrade-card-expert .upgrade-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
</style>

<style>
/* Professional upload modal (content is portaled) */
#professional-upload-modal .professional-upload-modal-header {
  background: #252840;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
#professional-upload-modal .professional-upload-modal-header .modal-title { color: #fff; }
#professional-upload-modal .professional-upload-modal-header .close { color: rgba(255, 255, 255, 0.8); }
#professional-upload-modal .professional-upload-modal-body {
  background: #252840;
  color: rgba(255, 255, 255, 0.95);
}
#professional-upload-modal .professional-upload-modal-footer {
  background: #252840;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
#professional-upload-modal .professional-upload-desc {
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  line-height: 1.5;
}
#professional-upload-modal .professional-upload-zone {
  border: 2px dashed rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}
#professional-upload-modal .professional-upload-zone:hover,
#professional-upload-modal .professional-upload-zone-dragover {
  border-color: rgba(99, 102, 241, 0.6);
  background: rgba(99, 102, 241, 0.08);
}
#professional-upload-modal .professional-upload-zone-has-file {
  border-style: solid;
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
}
#professional-upload-modal .professional-upload-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}
#professional-upload-modal .professional-upload-icon {
  font-size: 2.2rem;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
}
#professional-upload-modal .professional-upload-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 6px 0;
}
#professional-upload-modal .professional-upload-hint {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  margin: 0;
}
#professional-upload-modal .professional-upload-filename {
  color: rgba(255, 255, 255, 0.95);
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 12px 0;
  word-break: break-all;
}
#professional-upload-modal .professional-upload-remove {
  font-size: 13px;
  font-weight: 600;
  color: rgba(99, 102, 241, 0.95);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
}
#professional-upload-modal .professional-upload-remove:hover { text-decoration: underline; }
#professional-upload-modal .professional-upload-error {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f87171;
  font-size: 14px;
  font-weight: 600;
}
#professional-upload-modal .professional-doc-list {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 16px;
}
#professional-upload-modal .professional-doc-list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
#professional-upload-modal .professional-doc-list-title {
  color: rgba(255, 255, 255, 0.95);
  font-size: 13px;
  font-weight: 800;
  margin: 0;
}
#professional-upload-modal .professional-doc-list-refresh-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}
#professional-upload-modal .professional-doc-list-refresh-btn:disabled {
  opacity: 0.6;
}
#professional-upload-modal .professional-doc-list-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
}
#professional-upload-modal .professional-doc-list-empty {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}
#professional-upload-modal .professional-doc-list-table-wrap {
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
#professional-upload-modal .professional-doc-list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
#professional-upload-modal .professional-doc-list-table th,
#professional-upload-modal .professional-doc-list-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
#professional-upload-modal .professional-doc-list-table th {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 700;
}
#professional-upload-modal .professional-doc-list-table td {
  color: rgba(255, 255, 255, 0.9);
}
#professional-upload-modal .professional-doc-path {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
#professional-upload-modal .professional-doc-mlro {
  max-width: 200px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
}
#professional-upload-modal .professional-doc-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 600;
}
#professional-upload-modal .professional-doc-status-approved {
  background: rgba(34, 197, 94, 0.2);
  color: rgba(34, 197, 94, 0.95);
}
#professional-upload-modal .professional-doc-status-rejected {
  background: rgba(239, 68, 68, 0.2);
  color: rgba(239, 68, 68, 0.95);
}
#professional-upload-modal .professional-doc-status-pending {
  background: rgba(234, 179, 8, 0.2);
  color: rgba(234, 179, 8, 0.95);
}
</style>
